import { useQuery } from '@tanstack/react-query';

import type { TemplateResource, GetTemplateResourceRequest } from '@/types/template-resource';
import { getAuthToken } from '@/store/auth';
import queryKeys from '@/constants/query-keys';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { ApiResponse } from '@nf/types/api';
import { useLocale } from 'next-intl';
import { useGetSiteConfigs, useGetSiteConfigsWithSession } from '../use-site-configs';
import { convertCultureToRef } from '@/utils/common-util';
import { ApiBackendInfo, AthenaSiteConfig } from '../athena/types';
import { useApiBackendInfo } from '..';
import { productService } from '@/services/product.service';
import { LicenseeTypeEnums, SiteConfigsSettingsData, SiteConfigsSettingsWithSessionData } from '@nf/types/site';
import { useAthenaSiteConfig } from '../athena/use-athena-site-config';
import { useAccountInfo } from '@/hooks/query/account';
import { UserInfo } from '@nf/types/account';
import { useMemo } from 'react';
import { siteService } from '@/services/site.service';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';

const createApiUrl = (apiBackendInfo: ApiBackendInfo) => {
	return `${apiBackendInfo.url}/TemplateResource/GetTemplateResource`;
};

export const useTemplateResource = (productType: string, productId?: number) => {
	const isAthena = siteService.getLicenseeType() === LicenseeTypeEnums.Athena;
	const { SiteConfigsData: siteConfigData } = useGetSiteConfigs();
	const { SiteConfigsWithSessionData: siteConfigWithSession } = useGetSiteConfigsWithSession();
	const { data: athenaSiteConfig } = useAthenaSiteConfig();
	const { user } = useAccountInfo();
	const { enableESoccerBattle } = useLicSettings();

	const siteId = siteConfigData?.GetMessageID;
	const locale = useLocale();
	const languageName = convertCultureToRef(locale, true);
	const apiBackendInfo = useApiBackendInfo();

	const { data, isLoading, isError, error, isSuccess, refetch, isFetching } = useQuery({
		queryKey: [queryKeys.GET_TEMPLATE_RESOURCE, siteId, languageName, productType, productId],
		queryFn: () => fetchTemplateResource({
			SiteId: Number(siteId),
			LanguageName: languageName,
			ProductType: productType,
			ProductId: productId
		}, apiBackendInfo),
		staleTime: 1000 * 60 * 5,
		cacheTime: 1000 * 60 * 10,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		enabled: isAthena && Boolean(apiBackendInfo.isReady && siteId && languageName),
	});

	return {
		templateResource: useMemo(
			() =>
				filterResourceByConfigKey(
					data?.Data,
					siteConfigData,
					siteConfigWithSession,
					athenaSiteConfig,
					user,
					enableESoccerBattle ?? false
				),
			[data?.Data, siteConfigData, siteConfigWithSession, athenaSiteConfig, user, enableESoccerBattle]
		) as TemplateResource,
		isLoading,
		isError,
		error,
		isSuccess,
		refetch,
		isFetching
	}
};

const filterResourceByConfigKey = (
	templateResource?: TemplateResource | null | undefined,
	siteConfigData?: SiteConfigsSettingsData | null | undefined,
	siteConfigWithSession?: SiteConfigsSettingsWithSessionData | null | undefined,
	athenaSiteConfig?: AthenaSiteConfig | null | undefined,
	user?: UserInfo | null | undefined,
	enableESoccerBattle = false
): TemplateResource | null | undefined => {
	if (!templateResource || !siteConfigData || !siteConfigWithSession || !athenaSiteConfig) {
		return templateResource;
	}
	return {
		...templateResource,
		SubProductResources: templateResource.SubProductResources.filter(sub => {
			const param = `${sub.SportId},${sub.BetType},0`;
			return productService.canSee(
				'casino',
				param,
				siteConfigData,
				siteConfigWithSession,
				athenaSiteConfig,
				enableESoccerBattle,
				user,
				true,
			);
		}),
	};
};

export const fetchTemplateResource = async (getTemplateResourceRequest: GetTemplateResourceRequest, apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<TemplateResource>> => {
	const apiUrl = createApiUrl(apiBackendInfo);
	const authToken = getAuthToken();
	const response = await fetchComposer.postWithBearer(apiUrl, { body: JSON.stringify(getTemplateResourceRequest) })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`, { cause: response });
	}
	return await response.json();
};